"use client";
import { useCallback } from "react";
import { Box, CircularProgress, Skeleton } from "@mui/material";
import ActivitiesList from "./activities";
import CustomNewCard from "../city-landing/CustomNewCard";
import NoAvailableActivities from "../city-landing/NoAvailableActivities";
import GoogleTranslationComponent from "../googleTranslate/GoogleTranslationComponent";
import { CustomDiv, InlineDiv } from "@/common/divs";
import { RegularText } from "@/common/texts";
import { useTrack } from "@/hooks/useTrack";
import { ActivityDetailGeoSearch, ActivitySubCategory } from "@/model";
import { PRIMARY_BUTTON_COLOR } from "@/utils/constants";
import "@/components/city-landing/landing.css";

interface ActivitiesContainerProps {
  filteredActivities: { [key: string]: ActivityDetailGeoSearch[] };
  totalActivities: ActivityDetailGeoSearch[];
  isLoading: boolean;
  imInMapView?: boolean;
  locations?: {
    lat: number;
    lng: number;
    ref: React.RefObject<any>;
    subcategory: ActivitySubCategory;
    categoryId: number;
  }[];
  isMobile?: boolean;
  isRefetching?: boolean;
}

const ActivitiesContainer: React.FC<ActivitiesContainerProps> = ({
  filteredActivities,
  totalActivities,
  isLoading,
  imInMapView,
  locations,
  isMobile,
  isRefetching,
}) => {
  const { trackEvent, TrackCustomEvents } = useTrack();
  //check if totalActivities is an empty object

  const { searchParams } = new URL(window.location.href);
  const term = searchParams.get("term") ?? "";
  const checkTermString = useCallback(() => {
    //get the term from url
    //check if term is a param in the url
    //split the term by " " and check if there is a word that dosent start with cat: subcat: tag: or mob:
    //if there is a word that dosent start with the previous words return true
    //if there is no word that dosent start with the previous words return false

    if (term === "") return false;
    const splitedTerm = term.split(" ");
    let find = false;
    let i = 0;
    while (!find && i < splitedTerm.length) {
      if (
        !(
          splitedTerm[i].startsWith("cat:") ||
          splitedTerm[i].startsWith("subcat:") ||
          splitedTerm[i].startsWith("tag:") ||
          splitedTerm[i].startsWith("mob:")
        )
      ) {
        find = true;
      }
      i++;
    }
    return find;
  }, [term]);

  if (totalActivities.length == 0 && !isLoading && !isRefetching) {
    return <NoAvailableActivities />;
  }

  return (
    <Box>
      {isLoading || isRefetching ?
        <InlineDiv
          paddingY="30px"
          style={{ justifyContent: "center", minHeight: "200px" }}
        >
          <CustomDiv style={{ justifyContent: "center", textAlign: "center" }}>
            <CircularProgress
              style={{ color: PRIMARY_BUTTON_COLOR }}
              size={70}
            />
            <RegularText style={{ marginTop: "10PX" }}>
              <GoogleTranslationComponent text="Estamos buscando experiencias para ti, esto puede tardar unos segundos" />
            </RegularText>
          </CustomDiv>
          <Skeleton variant="rectangular" height={1800} animation={"wave"} />
        </InlineDiv>
      : checkTermString() ?
        <CustomDiv paddingY="15px">
          <InlineDiv
            className="filtered-activities-div"
            style={{
              display: "grid",
              gridTemplateColumns:
                isMobile ?
                  "repeat(auto-fit, minmax(350px, 1fr))"
                : "repeat(auto-fit, minmax(420px, 1fr))",
              gap: "20px",
              placeItems:
                isMobile ? "center"
                : totalActivities.length < 3 ? "start"
                : "center",
              paddingLeft:
                isMobile ? "0px"
                : totalActivities.length < 3 ? "80px"
                : "0px",
            }}
          >
            {totalActivities
              .sort((a, b) => a.price - b.price)
              .map((activity) => (
                <CustomNewCard
                  key={activity.id}
                  activityLink={
                    activity.slugEs ?
                      "/activity/" + activity.slugEs
                    : "/activity/" + activity.id
                  }
                  element={activity}
                />
              ))}
          </InlineDiv>
        </CustomDiv>
      : Object.keys(filteredActivities).map((category, index) => (
          <ActivitiesList
            isMobile={isMobile}
            imInMapView={imInMapView}
            key={category}
            title={category}
            activities={filteredActivities[category]}
            locations={locations}
            trackEvent={trackEvent}
            TrackCustomEvents={TrackCustomEvents}
            categoryId={
              //0 == Ultimas actividades, 1 == Actividades destacadas
              index > 1 ?
                category !== "Visitantes de crucero" ?
                  filteredActivities[category][0]?.category.id
                : undefined
              : undefined
            }
          />
        ))
      }
    </Box>
  );
};
export default ActivitiesContainer;
